/**
 * WorkItemType 数据服务
 */

import {
  injectable,
  inject,
  observable,
  action,
  runInAction,
  ICacheService,
  postConstruct,
} from '@meego-remote/core/app/contract'
import type { CacheService } from '@meego-remote/core/app/service'
import { IWorkItemTypeService, ISettingApiService, RPC } from '#contract'
import type { WorkItemType } from '../types'

/**
 * 获取项目下所有工作项类型
 */
@injectable({ implements: [IWorkItemTypeService] })
export class WorkItemTypeService implements IWorkItemTypeService {
  @inject(ISettingApiService)
  private settingApiService!: ISettingApiService

  @inject(ICacheService)
  private cacheService!: CacheService

  // Service 只管理数据状态，不管理 loading 状态
  @observable
  public workItemTypeList: WorkItemType[] = []

  // 过滤后的可用类型（排除 chart、sub_task、no_permission），用于 ScheduleLinkedSubWorkItem 等需要过滤的场景
  @observable
  public availableWorkItemTypes: WorkItemType[] = []

  // 记录当前正在进行的请求，防止并发重复请求
  private fetching: Map<string, Promise<WorkItemType[]>> = new Map()

  private get workItemTypeCache() {
    return this.cacheService?.getCacheGroup<WorkItemType[]>('WorkItemTypeService')
  }

  @postConstruct()
  protected init() {
    // 与 WorkItemRelationService 保持一致，设置为 5 分钟过期
    //
    // ⚠️ 风险说明：
    // - 本 Service 只负责数据拉取，不负责数据推送/更新
    // - 即使调用 getAll()，在缓存未过期时仍会优先返回缓存数据（被动过期机制）
    // - 如果其他模块在 meego-setting 中创建/修改工作项类型，本 Service 不会收到通知
    // - 存在数据强过期风险：其他模块修改后，本地缓存可能仍是旧数据
    // - 和主仓的主要差异：主仓是主动更新机制(切换空间/创建工作项/编辑工作项)
    //
    // ✅ 可接受理由：
    // - 工作项类型不是高频变更数据，5 分钟的过期窗口可接受
    // - 后续可考虑添加主动刷新机制（如事件通知、跨模块缓存同步等）
    this.workItemTypeCache?.setOptions({ defaultMaxAge: 5 * 60 * 1000 })
  }

  private getCacheKey(projectKey: string, needVirtual?: boolean): string {
    return `${projectKey}:${needVirtual ?? 'default'}`
  }

  @action('fetchWorkItemTypeList')
  public async getAll(projectKey: string, needVirtual?: boolean): Promise<WorkItemType[]> {
    if (!projectKey) {
      runInAction(() => {
        this.workItemTypeList = []
        this.availableWorkItemTypes = []
      })
      return []
    }

    // 构建缓存 key，包含 needVirtual 参数
    const cacheKey = this.getCacheKey(projectKey, needVirtual)

    // 使用缓存
    const cached = this.workItemTypeCache?.get(cacheKey)
    if (cached) {
      // 缓存命中时，同样需要过滤数据以保持一致性
      const filteredData = (cached || []).filter(
        (item) =>
          item.type_key !== 'chart'
          && item.type_key !== 'sub_task'
          && item.status !== 'no_permission',
      )
      runInAction(() => {
        this.workItemTypeList = cached
        this.availableWorkItemTypes = filteredData
      })
      // getAll() 语义：返回原始数据（全部），过滤逻辑仅影响 availableWorkItemTypes
      return cached
    }

    // 检查是否有正在进行的请求（防重）
    const existingRequest = this.fetching.get(cacheKey)
    if (existingRequest) {
      return existingRequest
    }

    // 创建新请求
    const request = this.settingApiService
      .request(RPC.APIGetWorkItemTypeInfoV2, { project_key: projectKey, need_virtual: needVirtual }) as Promise<WorkItemType[]>

    this.fetching.set(cacheKey, request)

    try {
      const data = await request
      // 与原组件保持一致的过滤：排除 chart、sub_task、no_permission
      const filteredData = (data || []).filter(
        (item) =>
          item.type_key !== 'chart'
          && item.type_key !== 'sub_task'
          && item.status !== 'no_permission',
      )
      // 更新缓存（存储原始数据，确保 workItemTypeList 能恢复原始数据）
      this.workItemTypeCache?.set(cacheKey, data)

      // await 之后的代码不在 @action 上下文中，需要使用 runInAction
      runInAction(() => {
        this.workItemTypeList = data
        this.availableWorkItemTypes = filteredData
      })
      return data
    }
    catch {
      // await 之后的代码不在 @action 上下文中，需要使用 runInAction
      runInAction(() => {
        this.workItemTypeList = []
        this.availableWorkItemTypes = []
      })
      return []
    }
    finally {
      // 请求完成后移除
      this.fetching.delete(cacheKey)
    }
  }
}
